/**
 * Onboarding state machine — shared contract.
 *
 * Single source of truth for the user-onboarding state set and the legal
 * transitions between states. Consumed by api (validation), x-agent (system
 * prompt + AGENTS.md section selection), and web (gate + directive
 * interceptor). See ADR 010 and the companion design spec.
 */
export const ONBOARDING_STATES = [
    'pending',
    'email_pitched',
    'email_connected',
    'email_skipped',
    'completed',
];
// email_connected is also a legal target from pending for SSO logins where email
// is connected as part of authentication (no separate pitch step required).
export const ONBOARDING_TRANSITIONS = {
    pending: ['email_pitched', 'email_connected'],
    email_pitched: ['email_connected', 'email_skipped'],
    email_connected: ['completed'],
    email_skipped: ['completed'],
    completed: [],
};
export function isOnboardingState(value) {
    return typeof value === 'string' && ONBOARDING_STATES.includes(value);
}
/**
 * Returns true when the user's onboarding is in a terminal / "done" state.
 * Handles both the new v2 terminal states AND the legacy v1 'completed' value
 * that existing users may still have persisted in the database.
 */
export function isOnboardingComplete(state) {
    return state === 'completed' || state === 'email_skipped';
}
/**
 * Legacy onboarding_state values from before the v2 state machine. Returning
 * users may still carry one of these in `platform.users.onboarding_state`.
 * Normalize at every read boundary (session/profile, advance route) so the v2
 * gate and transition logic only ever see canonical {@link OnboardingState}s.
 *
 * - `gmail_*` → the v2 `email_*` rename.
 * - `sync_complete` → a finished v1 onboarding ⇒ `completed` (otherwise these
 *   returning users get re-onboarded — the gate treats the unknown state as
 *   incomplete).
 * - pre-email funnel states (`plan_selected`, `name_collected`, …) → `pending`:
 *   they were all *before* email connection, so a clean restart at the v2 email
 *   pitch is the safe coercion (not a skip-ahead).
 */
const LEGACY_ONBOARDING_STATE = {
    gmail_connected: 'email_connected',
    gmail_skipped: 'email_skipped',
    sync_complete: 'completed',
    plan_selected: 'pending',
    name_collected: 'pending',
    profession_collected: 'pending',
    briefing_pitched: 'pending',
};
/**
 * Coerce a raw persisted onboarding_state into a canonical {@link OnboardingState}.
 * Unknown/empty values fall back to `pending`. Idempotent for canonical input.
 */
export function normalizeOnboardingState(raw) {
    if (isOnboardingState(raw))
        return raw;
    if (typeof raw === 'string' && raw in LEGACY_ONBOARDING_STATE) {
        return LEGACY_ONBOARDING_STATE[raw];
    }
    return 'pending';
}
/**
 * Maps onboarding states → chips rendered by OnboardingActions component.
 * States absent from this map render no chips.
 */
export const ONBOARDING_CHIP_ACTIONS = {
    // pending is shown when paywall is disabled (local dev) and PlanPicker never fires
    pending: [
        { label: 'Connect Gmail', sublabel: 'Gmail, Calendar, Drive', icon: 'google', kind: 'oauth.google.email', variant: 'primary' },
        { label: 'Connect Outlook', sublabel: 'Outlook, Calendar, OneDrive', icon: 'microsoft', kind: 'oauth.microsoft.outlook', variant: 'primary', microsoftOnly: true },
        { label: 'Show me demo data', sublabel: 'See Xavior with sample info', kind: 'demo.load', variant: 'outline' },
    ],
    email_pitched: [
        { label: 'Connect Gmail', sublabel: 'Gmail, Calendar, Drive', icon: 'google', kind: 'oauth.google.email', variant: 'primary' },
        { label: 'Connect Outlook', sublabel: 'Outlook, Calendar, OneDrive', icon: 'microsoft', kind: 'oauth.microsoft.outlook', variant: 'primary', microsoftOnly: true },
        { label: 'Show me demo data', sublabel: 'See Xavior with sample info', kind: 'demo.load', variant: 'outline' },
    ],
};
//# sourceMappingURL=onboarding.js.map